import { DEFAULT_JUDGE_SETTINGS, type JudgeSettings, type TimingGrade } from './judge/Judgement.js';
import type { SimulationState } from './SimulationState.js';

/**
 * What a finished run was, as a value.
 *
 * The Result screen reads this and nothing else. That is the whole point: the
 * simulation's state is *mutable and still running* — `MusicClock` keeps
 * advancing, the render loop keeps stepping, a wave can still expire into a
 * MISS — so a screen that read it live would show a score that quietly changed
 * while the player looked at it.
 *
 * So the run is converted to a value once, at the moment the outcome is
 * decided, and frozen. Everything derived from it — accuracy, the average
 * offset — is computed here too, not in the UI, so there is exactly one
 * definition of each and no second implementation to drift.
 */

export interface PlayResult {
  readonly packId: string;
  readonly songId: string;
  readonly displayName: string;
  readonly difficulty: string;
  readonly waveSpeed: number;

  readonly score: number;
  readonly maxCombo: number;

  readonly perfect: number;
  readonly great: number;
  readonly good: number;
  /** Failed taps. A single wave can contribute several. */
  readonly bad: number;
  readonly miss: number;

  readonly early: number;
  readonly late: number;
  /** Mean signed offset over tapped judgements, milliseconds. 0 when none. */
  readonly averageOffsetMs: number;
  readonly tappedJudgements: number;

  /** 0..1. See `accuracyOf`. */
  readonly accuracy: number;

  /** Events the chart holds. The denominator accuracy is measured against. */
  readonly totalEvents: number;
  /** Events that reached a verdict: cleared or missed. */
  readonly judgedEvents: number;
  /** Events that never became a wave because the field was full. */
  readonly droppedEvents: number;

  readonly durationSeconds: number;
  /** Song position when the result was taken. */
  readonly songTimeSeconds: number;
  /** Wall clock, milliseconds since the epoch. */
  readonly completedAt: number;
}

/**
 * Score earned against score available.
 *
 * ```
 * accuracy = score / (totalEvents * scores.perfect)
 * ```
 *
 * Two decisions worth stating, because both are arguable:
 *
 * **The denominator is every event in the chart**, not every event judged. A
 * wave that expired untouched is a MISS and counts against you; that is what a
 * miss *is*. Measuring against judged events only would let someone ignore half
 * a song and still read 100%.
 *
 * **BAD does not enter the denominator.** A failed tap leaves the wave standing
 * and it can still be cleared, so a wave is worth one perfect however many
 * attempts it took. BAD costs the combo, and it costs whatever grade the
 * eventual clear was worth — it does not additionally deflate the ceiling.
 */
export function accuracyOf(
  score: number,
  totalEvents: number,
  judge: JudgeSettings = DEFAULT_JUDGE_SETTINGS,
): number {
  const perfectScore = judge.scores.perfect;
  if (totalEvents <= 0 || perfectScore <= 0) return 0;
  const ceiling = totalEvents * perfectScore;
  const ratio = score / ceiling;
  return ratio < 0 ? 0 : ratio > 1 ? 1 : ratio;
}

/** Mean signed offset over tapped judgements. Negative is early. */
export function averageOffsetMs(sumMs: number, tapped: number): number {
  if (tapped <= 0 || !Number.isFinite(sumMs)) return 0;
  return sumMs / tapped;
}

export interface PlayContext {
  readonly packId: string;
  readonly songId: string;
  readonly displayName: string;
  readonly difficulty: string;
  readonly waveSpeed: number;
  readonly durationSeconds: number;
  /** Injectable so a test can pin the timestamp. */
  readonly now?: () => number;
}

/**
 * Takes the run's outcome as a frozen value.
 *
 * `Object.freeze` is not decoration. Between this call and the player pressing
 * RETRY there are several seconds in which the render loop, the audio clock and
 * the effect bus are all still live; freezing turns "the result must not change"
 * from a convention into something the runtime enforces.
 */
export function buildPlayResult(
  state: SimulationState,
  context: PlayContext,
  judge: JudgeSettings = DEFAULT_JUDGE_SETTINGS,
): PlayResult {
  const counts = state.judgeCounts;
  const grade = (name: TimingGrade): number => counts[name] ?? 0;
  const judgedEvents = grade('perfect') + grade('great') + grade('good') + grade('miss');
  const now = context.now ?? (() => Date.now());

  return Object.freeze({
    packId: context.packId,
    songId: context.songId,
    displayName: context.displayName,
    difficulty: context.difficulty,
    waveSpeed: context.waveSpeed,

    score: state.score,
    maxCombo: state.maxCombo,

    perfect: grade('perfect'),
    great: grade('great'),
    good: grade('good'),
    bad: grade('bad'),
    miss: grade('miss'),

    early: state.earlyCount,
    late: state.lateCount,
    averageOffsetMs: averageOffsetMs(state.timingOffsetSumMs, state.tappedJudgements),
    tappedJudgements: state.tappedJudgements,

    accuracy: accuracyOf(state.score, state.totalEvents, judge),

    totalEvents: state.totalEvents,
    judgedEvents,
    droppedEvents: state.droppedWaves,

    durationSeconds: context.durationSeconds,
    songTimeSeconds: state.songTime,
    completedAt: now(),
  });
}

/**
 * Whether the run is over and the outcome can no longer change.
 *
 * Deliberately *not* "the music stopped". A chart's last event can sit well
 * before the end of the audio, and its wave still has to expand, be tapped or
 * expire, and finish dissolving. Ending on the audio would cut the last note's
 * verdict; ending on the last judgement would cut the last dissolve.
 *
 * All four conditions, in the order they become true:
 *
 *   1. every chart event has been handed out
 *   2. no wave is still awaiting a verdict
 *   3. nothing is left on screen — the last dissolve has finished
 *   4. the song's own `endGraceSeconds` has elapsed past the final event
 */
export interface CompletionCheck {
  readonly state: SimulationState;
  readonly songTime: number;
  /** From the manifest. Unity's own trailing silence allowance. */
  readonly endGraceSeconds: number;
  /** Chart time of the last event, so the grace is measured from the music. */
  readonly lastEventTime: number;
}

export function isRunComplete(check: CompletionCheck): boolean {
  const { state } = check;
  if (!state.chartExhausted) return false;
  if (state.pendingWaves > 0) return false;
  if (state.activeWaves > 0) return false;
  if (state.notYetVisibleWaves > 0) return false;
  return check.songTime >= check.lastEventTime + check.endGraceSeconds;
}
